import type {
  DocumentBlock,
  DocumentTheme,
  FixedBlock,
  HeadingBlock,
  ResolvedDocument,
  SignatureBlock
} from "./types";
import { isHidden, textStyle } from "./types";

// A section's title, which flowOf adds as a heading in the h1 style; a heading block the map holds is never one.
export type SectionTitle = HeadingBlock & { sectionTitle: true };

// Signature blocks that follow one another, side by side: the row is spaced as its first block.
export type SignatureRow = SignatureBlock & { row: SignatureBlock[] };

export type FlowBlock =
  | Exclude<DocumentBlock, FixedBlock | SignatureBlock>
  | SectionTitle
  | SignatureRow;

export const isSectionTitle = (block: FlowBlock): block is SectionTitle =>
  block.type === "heading" &&
  "sectionTitle" in block &&
  block.sectionTitle === true;

// The document as one run of blocks: what pagination and measuring work on.
export type FlowItem =
  | { kind: "break"; background: string }
  | { kind: "fixed"; background: string; block: FixedBlock }
  | { kind: "block"; block: FlowBlock };

const numberedHeading = (
  heading: HeadingBlock,
  theme: DocumentTheme,
  prefix: string | undefined
): HeadingBlock =>
  prefix === undefined || !textStyle(theme, heading.style).numbered
    ? heading
    : { ...heading, text: [prefix, ...heading.text] };

// Sections in order: each starts its page, its title becomes the page heading, and
// numbers come from the section's place among numbered sections. Switched-off blocks are left out.
export const flowOf = (resolvedDocument: ResolvedDocument): FlowItem[] => {
  const items: FlowItem[] = [];
  let sectionNumber = 0;
  resolvedDocument.sections.forEach((section) => {
    const blocks = section.blocks.filter((block) => !isHidden(block));
    const fixed = blocks.find(
      (block): block is FixedBlock => block.type === "fixed"
    );
    if (fixed) {
      items.push({
        kind: "fixed",
        background: section.background,
        block: fixed
      });
      return;
    }
    // A fixed page is whole, so the section after it starts its own page, on its own background.
    const afterFixed = items[items.length - 1]?.kind === "fixed";
    if (section.pageBreakBefore || items.length === 0 || afterFixed) {
      items.push({ kind: "break", background: section.background });
    }
    const number = section.numbered ? (sectionNumber += 1) : undefined;
    if (section.title) {
      const text =
        number === undefined
          ? section.title
          : [`${number}. `, ...section.title];
      items.push({
        kind: "block",
        block: { type: "heading", style: "h1", text, sectionTitle: true }
      });
    }
    let subNumber = 0;
    // Signature blocks that follow one another fill rows of the signature theme's `columns`.
    let row: SignatureRow | undefined;
    const columns = resolvedDocument.theme.signature?.columns ?? 1;
    blocks.forEach((block) => {
      if (block.type === "fixed") return;
      if (block.type === "signature") {
        if (row && row.row.length < columns) row.row.push(block);
        else {
          row = { ...block, row: [block] };
          items.push({ kind: "block", block: row });
        }
        return;
      }
      row = undefined;
      if (
        block.type === "heading" &&
        number !== undefined &&
        textStyle(resolvedDocument.theme, block.style).numbered
      ) {
        subNumber += 1;
        items.push({
          kind: "block",
          block: numberedHeading(
            block,
            resolvedDocument.theme,
            `${number}.${subNumber} `
          )
        });
        return;
      }
      items.push({ kind: "block", block });
    });
  });
  return items;
};
